import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Autocomplete, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Grid, MenuItem, Radio, RadioGroup, Stack, Table, TableBody, TableCell,
  TableRow, TextField, Typography,
} from "@mui/material";
import { useAuth } from "../../../context/AuthContext";
import {
  buscarClientesEgresos, calcularMontos, crearCheque, fmtMoney,
} from "../services/egresosService";
import { fieldSx } from "./formStyles";
import { ComprobanteMarco, TituloSeccion } from "./ComprobanteUi";


const hoy = () => new Date().toISOString().slice(0, 10);

// Fuera del componente a propósito: si se declarara adentro se re-montaría en cada tecla y los inputs perderían el foco.
const Campo = ({ children, size = { xs: 12, sm: 6, md: 3 } }) => <Grid size={size}>{children}</Grid>;

const inicial = (catalogos, sucursal) => ({
  fecha: hoy(),
  cambio: catalogos.cambio ?? "",
  dirigido: "",
  deposito: "",
  num_cheque: "",
  ciudad: catalogos.ciudades.includes(sucursal) ? sucursal : catalogos.ciudades[0],
  num_contrato: "",
  num_referencia: "",
  servicio: "General",
  banco: catalogos.bancos.includes("BNB") ? "BNB" : catalogos.bancos[0],
  importe: "",
  moneda: "Bolivianos",
  concepto: null,
  mes: catalogos.meses[new Date().getMonth()],
  year: new Date().getFullYear(),
  observacion: "",
  conCliente: false,
  cliente: null,
  otro: "ninguno",
  retencion: "",
  factura: {
    fecha_factura: hoy(), nit: "", denominacion: "", nro_factura: "", nro_autorizacion: "",
    importe_total: "", importe_no_sujeto: "", descuento: "", cod_control: "",
  },
});

// Solo se elige de la lista; si el valor ya cargado no está en ella, se mantiene visible para no perderlo
const opcionesNombre = (nombres = [], actual) => (actual && !nombres.includes(actual) ? [actual, ...nombres] : nombres);

export default function ChequeForm({ catalogos, onGuardado }) {
  const { user } = useAuth();
  const [form, setForm] = useState(() => inicial(catalogos, user?.sucursal));
  const [error, setError] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState("");

  const [clientesOpciones, setClientesOpciones] = useState([]);
  const [clienteBusqueda, setClienteBusqueda] = useState("");

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  const setFactura = (campo) => (e) => setForm((f) => ({ ...f, factura: { ...f.factura, [campo]: e.target.value } }));

  useEffect(() => {
    if (!form.conCliente) return undefined;
    let activo = true;
    const t = setTimeout(() => {
      buscarClientesEgresos(clienteBusqueda)
        .then((res) => { if (activo) setClientesOpciones(res.data || []); })
        .catch(() => { if (activo) setClientesOpciones([]); });
    }, 300);
    return () => { activo = false; clearTimeout(t); };
  }, [form.conCliente, clienteBusqueda]);

  const retencionSel = useMemo(
    () => catalogos.retenciones.find((r) => r.nombre === form.retencion) || null,
    [catalogos.retenciones, form.retencion]
  );
  const montos = useMemo(
    () => calcularMontos({
      importe: form.importe,
      moneda: form.moneda,
      cambio: form.cambio,
      retencionPorcentaje: form.otro === "retencion" && retencionSel ? retencionSel.calculo : null,
    }),
    [form.importe, form.moneda, form.cambio, form.otro, retencionSel]
  );

  const visualizar = () => {
    setError("");
    if (!(Number(form.cambio) > 0)) return setError("No hay un tipo de cambio válido.");
    if (!form.dirigido.trim()) return setError("Falta indicar a quién va dirigido el cheque.");
    if (!form.banco) return setError("Falta el banco.");
    if (!(Number(String(form.importe).replace(",", ".")) > 0)) return setError("El importe debe ser mayor a 0.");
    if (!form.concepto) return setError("Falta seleccionar el concepto.");
    if (form.otro === "retencion" && !form.retencion) return setError("Selecciona el tipo de retención.");
    if (form.conCliente && !form.cliente) return setError("Selecciona el cliente o desmarca \"Con cliente\".");
    setErrorGuardar("");
    setPreviewOpen(true);
  };

  const guardar = async () => {
    setGuardando(true);
    setErrorGuardar("");
    try {
      const { data } = await crearCheque({
        fecha: form.fecha,
        cambio: form.cambio,
        dirigido: form.dirigido,
        deposito: form.deposito,
        num_cheque: form.num_cheque,
        ciudad: form.ciudad,
        num_contrato: form.num_contrato,
        num_referencia: form.num_referencia,
        servicio: form.servicio,
        banco: form.banco,
        importe: form.importe,
        moneda: form.moneda,
        conceptoId: form.concepto.id,
        mes: form.mes,
        year: form.year,
        observacion: form.observacion,
        otro: form.otro,
        retencion: form.retencion,
        factura: form.otro === "factura" ? form.factura : undefined,
        codServicioCliente: form.conCliente ? form.cliente?.id : undefined,
      });
      setPreviewOpen(false);
      setForm(inicial(catalogos, user?.sucursal));
      onGuardado(data.id);
    } catch (e) {
      setErrorGuardar(e?.response?.data?.error || "No se pudo guardar el cheque.");
    } finally {
      setGuardando(false);
    }
  };

  const years = [0, 1, 2, 3].map((i) => new Date().getFullYear() - 2 + i);

  return (
    <ComprobanteMarco titulo="Comprobante de egreso" fecha={form.fecha}>
      {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
      <Grid container spacing={0.6}>
        <TituloSeccion>Datos del comprobante</TituloSeccion>
        <Campo size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" type="date" label="Fecha de registro" value={form.fecha} onChange={set("fecha")} slotProps={{ inputLabel: { shrink: true } }} sx={fieldSx} /></Campo>
        <Campo size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="Tipo de cambio" value={form.cambio} onChange={set("cambio")} sx={fieldSx} /></Campo>

        <Campo size={12}>
          <Autocomplete size="small" options={opcionesNombre(catalogos.nombres, form.dirigido)} value={form.dirigido || null}
            onChange={(_, v) => setForm((f) => ({ ...f, dirigido: v || "" }))} noOptionsText="Sin resultados: el nombre debe estar en la lista"
            renderInput={(p) => <TextField {...p} label="Dirigido a" sx={fieldSx} />} />
        </Campo>
        <Campo size={12}>
          <Autocomplete size="small" options={opcionesNombre(catalogos.nombres, form.deposito)} value={form.deposito || null}
            onChange={(_, v) => setForm((f) => ({ ...f, deposito: v || "" }))} noOptionsText="Sin resultados: el nombre debe estar en la lista"
            renderInput={(p) => <TextField {...p} label="Depositado a" sx={fieldSx} />} />
        </Campo>

        <Campo size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="N° de cheque" value={form.num_cheque} onChange={set("num_cheque")} sx={fieldSx} /></Campo>
        <Campo size={{ xs: 12, md: 6 }}>
          <TextField select fullWidth size="small" label="Ciudad destino" value={form.ciudad} onChange={set("ciudad")} sx={fieldSx}>
            {catalogos.ciudades.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
        </Campo>
        <Campo size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="N° de contrato" value={form.num_contrato} onChange={set("num_contrato")} sx={fieldSx} /></Campo>
        <Campo size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="N° de referencia" value={form.num_referencia} onChange={set("num_referencia")} sx={fieldSx} /></Campo>
        <Campo size={{ xs: 12, md: 6 }}>
          <TextField select fullWidth size="small" label="Tipo de servicio" value={form.servicio} onChange={set("servicio")} sx={fieldSx}>
            {catalogos.servicios.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Campo>
        <Campo size={{ xs: 12, md: 6 }}>
          <TextField select fullWidth size="small" label="Banco" value={form.banco} onChange={set("banco")} sx={fieldSx}>
            {catalogos.bancos.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
          </TextField>
        </Campo>

        <Campo size={{ xs: 6, md: 3 }}><TextField fullWidth size="small" label="Importe" value={form.importe} onChange={set("importe")} sx={fieldSx} /></Campo>
        <Campo size={{ xs: 6, md: 3 }}>
          <TextField select fullWidth size="small" label="Moneda" value={form.moneda} onChange={set("moneda")} sx={fieldSx}>
            <MenuItem value="Bolivianos">Bolivianos</MenuItem>
            <MenuItem value="Dolares">Dólares</MenuItem>
          </TextField>
        </Campo>
        <Campo size={12}>
          <Autocomplete size="small" options={catalogos.conceptos} value={form.concepto}
            onChange={(_, v) => setForm((f) => ({ ...f, concepto: v }))}
            getOptionLabel={(o) => o?.concepto || ""} isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(p) => <TextField {...p} label="Concepto" sx={fieldSx} />} />
        </Campo>
        <Campo size={{ xs: 6, md: 4 }}>
          <TextField select fullWidth size="small" label="Mes de servicio" value={form.mes} onChange={set("mes")} sx={fieldSx}>
            {catalogos.meses.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
        </Campo>
        <Campo size={{ xs: 6, md: 2 }}>
          <TextField select fullWidth size="small" label="Año" value={form.year} onChange={set("year")} sx={fieldSx}>
            {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
        </Campo>

        <Campo size={{ xs: 12 }}><TextField fullWidth multiline minRows={2} maxRows={6} size="small" label="Observación" value={form.observacion} onChange={set("observacion")} sx={{ ...fieldSx, "& .MuiOutlinedInput-root": { ...fieldSx["& .MuiOutlinedInput-root"], minHeight: 28, py: "3px", alignItems: "flex-start" } }} /></Campo>

        <TituloSeccion>Opciones</TituloSeccion>
        <Grid size={{ xs: 12 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={form.conCliente} onChange={(e) => setForm((f) => ({ ...f, conCliente: e.target.checked, cliente: null }))} />}
              label={<Typography sx={{ fontSize: "0.8rem" }}>Con cliente</Typography>}
            />
            <RadioGroup row value={form.otro} onChange={set("otro")}>
              <FormControlLabel value="factura" control={<Radio size="small" />} label={<Typography sx={{ fontSize: "0.8rem" }}>Con factura</Typography>} />
              <FormControlLabel value="retencion" control={<Radio size="small" />} label={<Typography sx={{ fontSize: "0.8rem" }}>Retención</Typography>} />
              <FormControlLabel value="ninguno" control={<Radio size="small" />} label={<Typography sx={{ fontSize: "0.8rem" }}>Ninguno</Typography>} />
            </RadioGroup>
          </Stack>
        </Grid>

        {form.conCliente && (
          <Campo size={{ xs: 12 }}>
            <Autocomplete size="small" options={clientesOpciones} value={form.cliente}
              onChange={(_, v) => setForm((f) => ({ ...f, cliente: v }))}
              onInputChange={(_, v, reason) => { if (reason === "input") setClienteBusqueda(v); }}
              filterOptions={(x) => x}
              getOptionLabel={(o) => `${o.razon_social} ${o.servicio}${o.descripcion ? ` - ${o.descripcion}` : ""}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="Escribe para buscar un cliente"
              renderInput={(p) => <TextField {...p} label="Buscar cliente" sx={fieldSx} />} />
          </Campo>
        )}

        {form.otro === "retencion" && (
          <Grid size={{ xs: 12 }}>
            <RadioGroup row value={form.retencion} onChange={set("retencion")}>
              {catalogos.retenciones.map((r) => (
                <FormControlLabel key={r.id} value={r.nombre} control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: "0.8rem" }}>{r.nombre} ({r.calculo}%)</Typography>} />
              ))}
            </RadioGroup>
          </Grid>
        )}

        {form.otro === "factura" && (
          <>
            <Campo><TextField fullWidth size="small" type="date" label="Fecha de la factura" value={form.factura.fecha_factura} onChange={setFactura("fecha_factura")} slotProps={{ inputLabel: { shrink: true } }} sx={fieldSx} /></Campo>
            <Campo><TextField fullWidth size="small" label="NIT" value={form.factura.nit} onChange={setFactura("nit")} sx={fieldSx} /></Campo>
            <Campo size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" label="Nombre o denominación" value={form.factura.denominacion} onChange={setFactura("denominacion")} sx={fieldSx} /></Campo>
            <Campo><TextField fullWidth size="small" label="N° de factura" value={form.factura.nro_factura} onChange={setFactura("nro_factura")} sx={fieldSx} /></Campo>
            <Campo><TextField fullWidth size="small" label="N° de autorización" value={form.factura.nro_autorizacion} onChange={setFactura("nro_autorizacion")} sx={fieldSx} /></Campo>
            <Campo><TextField fullWidth size="small" label="Importe total" value={form.factura.importe_total} onChange={setFactura("importe_total")} sx={fieldSx} /></Campo>
            <Campo><TextField fullWidth size="small" label="Importe no sujeto a crédito fiscal" value={form.factura.importe_no_sujeto} onChange={setFactura("importe_no_sujeto")} sx={fieldSx} /></Campo>
            <Campo><TextField fullWidth size="small" label="Descuento" value={form.factura.descuento} onChange={setFactura("descuento")} sx={fieldSx} /></Campo>
            <Campo><TextField fullWidth size="small" label="Código de control" value={form.factura.cod_control} onChange={setFactura("cod_control")} sx={fieldSx} /></Campo>
          </>
        )}

        <Grid size={{ xs: 12 }} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button size="small" variant="outlined" color="inherit" sx={{ borderRadius: 0 }} onClick={() => { setForm(inicial(catalogos, user?.sucursal)); setError(""); }}>
            Borrar
          </Button>
          <Button size="small" variant="contained" sx={{ borderRadius: 0 }} onClick={visualizar}>Visualizar</Button>
        </Grid>
      </Grid>

      <Dialog open={previewOpen} onClose={() => !guardando && setPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>Comprobante de egreso</DialogTitle>
        <DialogContent dividers>
          {errorGuardar && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{errorGuardar}</Alert>}
          <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.3, px: 0.6, fontSize: 12 } }}>
            <TableBody>
              {[
                ["Fecha", form.fecha], ["Tipo de cambio", form.cambio], ["Dirigido a", form.dirigido], ["Depositado a", form.deposito || "-"],
                ["N° de cheque", form.num_cheque || "-"], ["Ciudad", form.ciudad], ["Banco", form.banco], ["Servicio", form.servicio],
                ["Concepto", form.concepto?.concepto], ["Mes de servicio", `${form.mes} del ${form.year}`],
                ["Importe en Bolivianos", fmtMoney(montos.bolivianos)], ["Importe en Dólares", fmtMoney(montos.dolares)],
                ...(form.otro === "retencion" ? [["Retención", `${form.retencion} — ${fmtMoney(montos.montoRetencion)}`], ["Importe total", fmtMoney(montos.importeTotal)]] : []),
                ...(form.otro === "factura" && form.factura.nro_factura ? [["Factura", `${form.factura.nro_factura} (${form.factura.denominacion || "-"})`]] : []),
                ...(form.conCliente && form.cliente ? [["Cliente", `${form.cliente.razon_social} ${form.cliente.servicio}`]] : []),
                ["Observación", form.observacion || "-"],
              ].map(([k, v]) => (
                <TableRow key={k}>
                  <TableCell sx={{ fontWeight: 700, width: "38%", bgcolor: "action.hover" }}>{k}</TableCell>
                  <TableCell>{v}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ p: 0.75 }}>
          <Button size="small" onClick={() => setPreviewOpen(false)} disabled={guardando}>Volver</Button>
          <Button size="small" variant="contained" onClick={guardar} disabled={guardando}>
            {guardando ? <CircularProgress size={16} color="inherit" /> : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </ComprobanteMarco>
  );
}
